import Phaser from 'phaser';
import { GameScene, type ActiveAnimation } from '../GameScene';
import { AnimPanel } from '../ui/AnimPanel';
import { playAnimationAt } from '../../sprites/AnimationSpriteManager';
import { ClientMessageType, type PlayAnimationMessage } from '@shared/types/messages';

/**
 * Panel de dev de animaciones (Ctrl+; ) + handler de PLAY_ANIMATION: reproduce una animación sobre
 * una entidad y permite dispararlas manualmente. Extraído de GameScene (god-object). El estado
 * (animPanel, animationsIndex) vive en GameScene (host) y se accede vía `this.scene.*`.
 */
export class AnimationPanel {
  constructor(private scene: GameScene) {}

  public handlePlayAnimation(message: PlayAnimationMessage): void {
    const playerObj = this.scene.playerObjects.get(message.entityId);

    if (!playerObj) return;

    // Obtener offsetY del índice de animaciones

    const animKey = `animation${message.animationId}`;

    const animDef = this.scene.animationsIndex?.[animKey];

    const offsetY = animDef?.offsetY ?? 0;

    // Obtener posición del sprite del jugador

    const x = playerObj.sprite.x;

    const y = playerObj.sprite.y + offsetY;

    // Calcular depth: arriba o debajo del personaje

    const depth = message.back 

      ? playerObj.sprite.depth - 1 

      : playerObj.sprite.depth + 100;

    const animSprite = playAnimationAt(this.scene, message.animationId, x, y, message.loop, depth);

    if (!animSprite) return;

    // Agregar al array de animaciones activas

    const activeAnim: ActiveAnimation = { sprite: animSprite, back: message.back, offsetY };

    playerObj.activeAnimations.push(activeAnim);

    // Cuando termine, remover del array

    if (!message.loop) {
      animSprite.once('animationcomplete', () => {
        const idx = playerObj.activeAnimations.indexOf(activeAnim);

        if (idx !== -1) {
          playerObj.activeAnimations.splice(idx, 1);
        }
      });
    }
  }

  // -----------------

  // Panel de Animaciones

  // -----------------

  public createAnimPanel(): void {
    const { width, height } = this.scene.scale;
    const savedOptions = this.scene.persistence.loadClientOptions();
    const panelX = savedOptions?.animPanelX ?? width / 2;
    const panelY = savedOptions?.animPanelY ?? height / 2;

    this.scene.animPanel = new AnimPanel(
      this.scene,
      {
        onMove: (x, y) => this.scene.persistence.savePanelPosition('anim', x, y),
        onPlayAnimation: (animationId) => this.sendPlayAnimationCommand(animationId),
      },
      { initialX: panelX, initialY: panelY }
    );
    if (this.scene.animationsIndex) {
      this.scene.animPanel.setAnimationsIndex(this.scene.animationsIndex);
    }
  }

  public sendPlayAnimationCommand(animationId: number): void {
    if (!this.scene.colyseusClient) return;
    this.scene.colyseusClient.send({
      type: ClientMessageType.PLAY_ANIMATION,
      animationId,
    } as never);
  }

  public openAnimPanel(): void {
    if (!this.scene.animPanel) {
      this.createAnimPanel();
    }
    this.scene.animPanel.open();
  }

  public closeAnimPanel(): void {
    this.scene.animPanel?.close();
  }

  public toggleAnimPanel(): void {
    if (!this.scene.animPanel) {
      this.createAnimPanel();
    }
    this.scene.animPanel.toggle();
  }

}
